// Pembayaran bagi (split payment) di kasir web: tunai + non-tunai, atau beberapa non-tunai.
// Aturan SAMA dengan server (backend utils/bagiBayar.js) dan aplikasi HP; server tetap memeriksa ulang terhadap total
// yang dihitungnya.
//  - non-tunai tidak boleh melebihi tagihan (tidak ada kembalian non-tunai),
//  - ada bagian tunai: tunai dipakai = tagihan - total non-tunai, uang diterima harus cukup, kelebihannya = kembalian,
//  - tanpa bagian tunai: total non-tunai harus sama dengan tagihan.
// QRIS Midtrans tidak bisa dibagi.
export const MAKS_BAGIAN = 5;
const TOLERANSI = 0.01;
const rp = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const angka = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const bagiAwal = () => ({ non: [{ uuid: '', nominal: '' }], tunai: '' });

/**
 * Hitung & periksa rencana bagi.
 * @param total tagihan akhir (setelah promo & PPN)
 * @param bagi { non: [{ uuid, nominal }], tunai: string }
 * @param metodeNonTunai daftar metode non-tunai [{ uuid, name }]
 * @returns { galat, totalNon, tunaiDiterima, tunaiDipakai, kembalian, metodeUtama, payload, baris }
 */
export const hitungBagi = (total, bagi, metodeNonTunai) => {
  const tagihan = Number(total) || 0;
  const non = (bagi?.non || [])
    .filter((b) => b.uuid || angka(b.nominal) > 0)
    .map((b) => ({ uuid: b.uuid, nama: (metodeNonTunai.find((m) => m.uuid === b.uuid) || {}).name || '', nominal: angka(b.nominal) }));
  const tunaiDiterima = angka(bagi?.tunai);
  const adaTunai = tunaiDiterima > 0;
  const totalNon = non.reduce((a, b) => a + b.nominal, 0);
  const jumlahBagian = non.length + (adaTunai ? 1 : 0);

  let galat = null;
  if (jumlahBagian < 2) galat = 'Pilih minimal dua bagian pembayaran';
  else if (jumlahBagian > MAKS_BAGIAN) galat = `Maksimal ${MAKS_BAGIAN} bagian pembayaran`;
  else if (non.some((b) => b.nominal <= 0)) galat = 'Nominal non-tunai harus lebih dari 0';
  else if (non.some((b) => !b.nama)) galat = 'Pilih metode untuk setiap bagian non-tunai';
  else if (totalNon > tagihan + TOLERANSI) galat = 'Non-tunai melebihi tagihan';
  else if (!adaTunai) {
    if (Math.abs(totalNon - tagihan) > TOLERANSI) galat = `Non-tunai kurang ${rp(tagihan - totalNon)} dari tagihan`;
  } else {
    const sisa = tagihan - totalNon;
    if (sisa <= TOLERANSI) galat = 'Non-tunai sudah menutup tagihan; kosongkan uang tunai';
    else if (tunaiDiterima + TOLERANSI < sisa) galat = `Uang tunai kurang ${rp(sisa - tunaiDiterima)}`;
  }

  const tunaiDipakai = adaTunai ? tagihan - totalNon : 0;
  const kembalian = adaTunai ? Math.max(0, tunaiDiterima - tunaiDipakai) : 0;
  const payload = [
    ...non.map((b) => ({ metode: 'noncash', nonCashMethodName: b.nama, nominal: b.nominal })),
    ...(adaTunai ? [{ metode: 'cash', nominal: tunaiDiterima }] : []),
  ];
  return {
    galat,
    sah: !galat,
    totalNon,
    tunaiDiterima,
    tunaiDipakai,
    kembalian,
    metodeUtama: adaTunai ? 'cash' : 'noncash',
    payload,
    baris: barisBagi(payload, tagihan),
  };
};

/** Baris struk: tiap metode, uang diterima & kembalian bila ada tunai. Menerima bentuk kirim ATAU rincian dari server. */
export const barisBagi = (bagian, total) => {
  // QRIS (bagian dari transaksi dengan DP reservasi) dicetak seperti non-tunai bernama "QRIS".
  const non = (bagian || []).filter((b) => b.metode === 'noncash' || b.metode === 'qris');
  const namaNon = (b) => b.nonCashMethodName || (b.metode === 'qris' ? 'QRIS' : 'Non-tunai');
  const tunai = (bagian || []).find((b) => b.metode === 'cash');
  const sumNon = non.reduce((a, b) => a + angka(b.nominal), 0);
  const baris = [];
  if (tunai) {
    const diterima = angka(tunai.diterima ?? tunai.nominal);
    const dipakai = total - sumNon;
    baris.push(`Tunai: ${rp(dipakai)}`);
    non.forEach((b) => baris.push(`${namaNon(b)}: ${rp(b.nominal)}`));
    if (diterima - dipakai > 0.5) {
      baris.push(`Uang diterima: ${rp(diterima)}`);
      baris.push(`Kembalian: ${rp(diterima - dipakai)}`);
    }
  } else {
    non.forEach((b) => baris.push(`${namaNon(b)}: ${rp(b.nominal)}`));
  }
  return baris;
};
